/**
 * @file plan.service.ts
 * @location src/app/subscription-management/plan-services/plan.service.ts
 *
 * PlanService
 * -----------
 * Builds a single, reactive `VendorPermissions` object for the currently
 * authenticated vendor by composing:
 *
 *   1. AngularFire Firestore (docData) – live stream of users-list/{uid}
 *   2. Auth (AngularFire)             – supplies the current user's UID
 *   3. SubscriptionPlanService        – live stream of subscription_plans
 *
 * The public surface is ONE observable: `permissions$`.
 *
 * Changes from previous version:
 *   - userProfile$ now uses docData() for a live Firestore stream instead
 *     of a one-shot Promise via FirestoreService.getUserProfile().
 *   - planWithProfile$ resolves by planId (document ID) only. The legacy
 *     name-based fallback (p.name === planId) has been removed.
 *   - VendorPermissions now includes `listingFee` so components no longer
 *     need independent Firestore reads to obtain it.
 *   - NULL_PERMISSIONS updated to include listingFee: 0.
 *   - computePermissions maps plan.listingFee directly (no `as any` cast
 *     since AdminPlan.listingFee is already typed in subscription-plan.service.ts).
 */

import { Injectable } from '@angular/core';
import { Auth, authState, User } from '@angular/fire/auth';
import {
  Firestore,
  doc,
  docData,
  getDoc,
} from '@angular/fire/firestore';
import { combineLatest, Observable, of } from 'rxjs';
import { filter, map, shareReplay, switchMap } from 'rxjs/operators';

// ─── Peer services (DO NOT MODIFY) ──────────────────────────────────────────
import {
  AdminPlan,
  SubscriptionPlanService,
} from '../subscription-services/subscription-plan.service';

// ─── Firestore document shape ────────────────────────────────────────────────

/**
 * Shape of the `users-list/{uid}` Firestore document.
 * Only fields consumed by this service are declared; the index signature
 * lets extra Firestore fields pass through without TypeScript errors.
 */
interface UserProfile {
  plan?: string;
  planId?: string;
  subscriptionExpiry?: string | null;
  subscriptionStatus?: string;
  [key: string]: unknown;
}

// ─── Public output shape ─────────────────────────────────────────────────────

/**
 * The computed permissions object emitted by `permissions$`.
 *
 * Consumers must read permissions from this interface only — never from raw
 * Firestore documents — so all business rules stay centralised in one place.
 */
export interface VendorPermissions {
  /** true when subscriptionStatus === 'active' OR subscriptionExpiry is a future date */
  subscriptionActive: boolean;

  /** true when subscriptionExpiry is a past date and status !== 'active' */
  subscriptionExpired: boolean;

  /** subscriptionActive AND AdminPlan.marketplaceEnabled */
  marketplaceEnabled: boolean;

  /** subscriptionActive AND AdminPlan.listedEquipmentModuleEnabled */
  listedEquipmentModuleEnabled: boolean;

  /** AdminPlan.activeListingLimit — max concurrent equipment listings */
  maxListings: number;

  /** AdminPlan.bookingLimit — max bookings per billing cycle */
  bookingLimit: number;

  /** AdminPlan.listingDurationDays — days a listing stays live */
  listingDays: number;

  /** AdminPlan.storageLimitGb — cloud storage quota */
  storageLimitGb: number;

  /** AdminPlan.listingFee — per-listing fee in AED (eliminates duplicate reads) */
  listingFee: number;

  /** AdminPlan.listedEquipmentCap — number of free marketplace slots included in the plan */
  listedEquipmentCap: number;

  /** AdminPlan.trialEnabled — free trial plan flag */
  trialEnabled: boolean;

  /** AdminPlan.trialVehicleCap — max marketplace slots while on trial */
  trialVehicleCap: number;

  /** The Firestore document ID stored in users-list/{uid}.planId */
  currentPlanId: string;

  /** AdminPlan.name — human-readable plan name */
  currentPlanName: string;

  /** Raw ISO-8601 expiry string, or null when no subscription record exists */
  subscriptionExpiry: string | null;
}

// ─── Sentinel / fallback ─────────────────────────────────────────────────────

/**
 * Safe zero-value returned when the vendor has no plan or no subscription.
 * Every field defaults to its most-restrictive value so access is denied
 * until a valid plan is resolved.
 */
const NULL_PERMISSIONS: VendorPermissions = {
  subscriptionActive: false,
  subscriptionExpired: false,
  marketplaceEnabled: false,
  listedEquipmentModuleEnabled: false,
  maxListings: 0,
  bookingLimit: 0,
  listingDays: 0,
  storageLimitGb: 0,
  listingFee: 0,
  listedEquipmentCap: 0,
  trialEnabled: false,
  trialVehicleCap: 0,
  currentPlanId: '',
  currentPlanName: '',
  subscriptionExpiry: null,
};

// ─── Service ─────────────────────────────────────────────────────────────────

@Injectable({ providedIn: 'root' })
export class PlanService {

  /**
   * PUBLIC API — `permissions$`
   * ----------------------------
   * A hot, shared observable that emits a fresh `VendorPermissions` object
   * whenever any of the following change:
   *
   *   • The authenticated user (login / logout)
   *   • The `users-list/{uid}` Firestore document  (live via docData)
   *   • The `subscription_plans` collection        (live via collectionData)
   *
   * `shareReplay({ bufferSize: 1, refCount: true })` means:
   *   – All subscribers share one Firestore pipeline (no duplicate reads).
   *   – A late subscriber immediately receives the last computed value.
   *   – When subscriber count drops to zero, upstream listeners are released.
   */
  readonly permissions$: Observable<VendorPermissions>;

  constructor(
    private readonly auth: Auth,
    private readonly firestore: Firestore,
    private readonly subscriptionPlanService: SubscriptionPlanService,
  ) {
    this.permissions$ = this.buildPermissions$();
  }

  // ─── Private pipeline ────────────────────────────────────────────────────

  /**
   * `buildPermissions$()`
   * ---------------------
   * Wires the full reactive chain. Called once in the constructor.
   *
   * Chain:
   *   authState → filter(signed-in) → userProfile$ → planWithProfile$ → computePermissions
   *
   * `switchMap` at every async step ensures in-flight inner subscriptions
   * are cancelled when the source emits a new value (e.g. re-login).
   */
  private buildPermissions$(): Observable<VendorPermissions> {
    return authState(this.auth).pipe(

      // Ignore null (signed-out) — stream stays silent until a user logs in.
      filter((user): user is User => user !== null),

      // ── WORKER SUPPORT ─────────────────────────────────────────────────────
      // If the logged-in user is a worker, resolve their parentUid first and
      // use that to stream the parent vendor's document. This ensures that all
      // plan-gated features (Listed Equipment, Marketplace, Drive) work for
      // workers exactly as they do for the vendor.
      switchMap((user: User) => this.resolveEffectiveUid$(user.uid)),
      // ── END WORKER SUPPORT ─────────────────────────────────────────────────

      // Open a live Firestore stream for users-list/{effectiveUid}.
      // userProfile$() internally filters undefined/null (docData emits undefined
      // when the document does not exist — e.g. phone-number users navigating
      // before their Firestore document is fully written).
      switchMap((uid: string) => this.userProfile$(uid)),

      // Load the matching AdminPlan and pair it with the profile.
      switchMap((profile: UserProfile) => this.planWithProfile$(profile)),

      // Apply business rules → emit VendorPermissions.
      map(([profile, plan]: [UserProfile, AdminPlan | null]) =>
        this.computePermissions(profile, plan),
      ),

      // Multicast + replay last value for late subscribers.
      shareReplay({ bufferSize: 1, refCount: true }),
    );
  }

  /**
   * `resolveEffectiveUid$(uid)`
   * ---------------------------
   * If the user's document has role === 'worker' and a parentUid field, return
   * an Observable of the parentUid. Otherwise return the user's own uid.
   * Uses a one-shot getDoc so the resolution is fast and does not add a
   * permanent listener (the vendor profile listener below handles live updates).
   */
  private resolveEffectiveUid$(uid: string): Observable<string> {
    return new Observable<string>(subscriber => {
      getDoc(doc(this.firestore, `users-list/${uid}`)).then(snap => {
        if (snap.exists()) {
          const data = snap.data() as any;
          if (data.role === 'worker' && data.parentUid) {
            subscriber.next(data.parentUid);
          } else {
            subscriber.next(uid);
          }
        } else {
          subscriber.next(uid);
        }
        subscriber.complete();
      }).catch(() => {
        subscriber.next(uid);
        subscriber.complete();
      });
    });
  }

  /**
   * `userProfile$(uid)`
   * -------------------
   * Returns a LIVE Firestore stream of the users-list/{uid} document using
   * `docData()`.  Unlike the previous `from(Promise)` approach, this emits
   * every time the document changes in Firestore, so admin plan edits and
   * subscription status changes propagate immediately without a page reload.
   *
   * Note: FirestoreService.getUserProfile is no longer used here; that
   * dependency has been removed from the constructor.
   *
   * @param uid  Firebase Auth UID of the signed-in user.
   * @returns    Observable that emits the UserProfile on every document change.
   */
  private userProfile$(uid: string): Observable<UserProfile> {
    const ref = doc(this.firestore, `users-list/${uid}`);
    // docData() emits `undefined` when the Firestore document does not exist.
    // This happens for phone-number authenticated users whose document is written
    // after OTP verification completes and the app has already navigated.
    // Cast to unknown first, then filter nullish values before typing as UserProfile.
    return (docData(ref) as Observable<unknown>).pipe(
      filter((data): data is UserProfile => data !== undefined && data !== null),
    );
  }

  /**
   * `planWithProfile$(profile)`
   * ---------------------------
   * Looks up the vendor's plan inside the `AdminPlan[]` list emitted by
   * `SubscriptionPlanService.getPlans()`, matching by Firestore document ID
   * (planId) only.
   *
   * Legacy behaviour removed:
   *   The previous implementation fell back to matching by plan display name
   *   (`p.name === planId`). This has been removed because:
   *     1. It can silently resolve to the wrong plan if names collide.
   *     2. All active vendors should have a `planId` (document ID) field.
   *     3. Vendors without planId will see the locked screen, surfacing
   *        unmigrated records rather than granting unintended access.
   *
   * Why `combineLatest`?
   *   If an admin updates a plan in Firestore, `getPlans()` emits a new array.
   *   `combineLatest` re-combines immediately so permissions update in real
   *   time without the vendor needing to refresh.
   *
   * @param profile  The already-resolved UserProfile.
   * @returns        Observable of [profile, AdminPlan | null] tuple.
   */
  private planWithProfile$(
    profile: UserProfile,
  ): Observable<[UserProfile, AdminPlan | null]> {

    const planId: string = (profile?.planId as string) ?? '';

    if (!planId) {
      // No planId present — vendor has not been migrated to the ID-based
      // system. Return null so computePermissions emits NULL_PERMISSIONS and
      // the vendor sees the locked/upgrade screen.
      return of([profile, null] as [UserProfile, AdminPlan | null]);
    }

    const plan$: Observable<AdminPlan | null> =
      this.subscriptionPlanService.getPlans().pipe(
        map((plans: AdminPlan[]) =>
          // Match exclusively by Firestore document ID.
          // AdminPlan.id is populated from docId via { idField: 'docId' }
          // in SubscriptionPlanService.getPlans().
          plans.find((p: AdminPlan) => p.id === planId) ?? null
        ),
      );

    return combineLatest([of(profile), plan$]);
  }

  /**
   * `computePermissions(profile, plan)`
   * ------------------------------------
   * Pure function — no Firestore calls, no side effects.
   * Single source of truth for all permission rules.
   *
   * Field mapping from AdminPlan → VendorPermissions:
   *
   *   AdminPlan field                  → VendorPermissions field
   *   ─────────────────────────────────────────────────────────────
   *   p.activeListingLimit             → maxListings
   *   p.listingDurationDays            → listingDays
   *   p.bookingLimit                   → bookingLimit
   *   p.storageLimitGb                 → storageLimitGb
   *   p.listingFee                     → listingFee
   *   p.marketplaceEnabled             → marketplaceEnabled  (gated by subscriptionActive)
   *   p.listedEquipmentModuleEnabled   → listedEquipmentModuleEnabled (gated)
   *   p.name                           → currentPlanName
   *   p.id                             → currentPlanId
   *
   * Business rules:
   *   subscriptionActive  = status === 'active' OR (expiry exists AND expiry > now)
   *   subscriptionExpired = status !== 'active' AND expiry exists AND expiry <= now
   *   marketplaceEnabled  = subscriptionActive AND plan.marketplaceEnabled
   *   listedEquipmentModuleEnabled = subscriptionActive AND plan.listedEquipmentModuleEnabled
   */
  private computePermissions(
    profile: UserProfile,
    plan: AdminPlan | null,
  ): VendorPermissions {

    const rawExpiry: any = profile?.subscriptionExpiry;
    let subscriptionExpiry: string | null = null;
    let expiryMs: number = NaN;
    let hasExpiry = false;

    if (rawExpiry) {
      if (typeof rawExpiry.toDate === 'function') {
        const d = rawExpiry.toDate();
        subscriptionExpiry = d.toISOString();
        expiryMs = d.getTime();
        hasExpiry = true;
      } else if (typeof rawExpiry === 'string' && rawExpiry.length > 0) {
        subscriptionExpiry = rawExpiry;
        expiryMs = Date.parse(rawExpiry);
        hasExpiry = !isNaN(expiryMs);
      } else if (rawExpiry instanceof Date) {
        subscriptionExpiry = rawExpiry.toISOString();
        expiryMs = rawExpiry.getTime();
        hasExpiry = true;
      }
    }

    const subscriptionStatus: string =
      (profile?.subscriptionStatus as string) ?? '';

    const nowMs: number = Date.now();

    const subscriptionActive: boolean =
      subscriptionStatus === 'active' ||
      (hasExpiry && !isNaN(expiryMs) && expiryMs > nowMs);

    const subscriptionExpired: boolean =
      subscriptionStatus !== 'active' &&
      hasExpiry && !isNaN(expiryMs) && expiryMs <= nowMs;

    // No plan resolved — return restricted defaults, preserving subscription
    // state so the locked screen can still show accurate expiry information.
    if (!plan) {
      return {
        ...NULL_PERMISSIONS,
        subscriptionActive,
        subscriptionExpired,
        subscriptionExpiry,
        currentPlanId: (profile?.planId as string) ?? '',
      };
    }

    const marketplaceEnabled: boolean =
      subscriptionActive && (
        plan.marketplaceEnabled === true ||
        (plan.marketplaceEnabled as any) === 'true'
      );

    const listedEquipmentModuleEnabled: boolean =
      subscriptionActive && (
        plan.listedEquipmentModuleEnabled === true ||
        (plan.listedEquipmentModuleEnabled as any) === 'true'
      );

    return {
      subscriptionActive,
      subscriptionExpired,
      marketplaceEnabled,
      listedEquipmentModuleEnabled,
      maxListings:    plan.activeListingLimit  ?? 0,
      bookingLimit:   plan.bookingLimit        ?? 0,
      listingDays:    plan.listingDurationDays ?? 0,
      storageLimitGb: plan.storageLimitGb      ?? 0,
      listingFee:          plan.listingFee          ?? 0,
      listedEquipmentCap:  plan.listedEquipmentCap  ?? 0,
      trialEnabled:        plan.trialEnabled        ?? false,
      trialVehicleCap:     plan.trialVehicleCap     ?? 0,
      currentPlanId:  plan.id,
      currentPlanName: plan.name ?? '',
      subscriptionExpiry,
    };
  }
}